<template>
  <div
    v-if="isVisible"
    class="modal estimate-status-transition-modal"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    aria-labelledby="estimateStatusTransitionModalLabel"
    @keydown.esc="closeModal"
  >
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 id="estimateStatusTransitionModalLabel" class="modal-title">{{ title }}</h5>
          <button
            type="button"
            class="btn-close"
            aria-label="閉じる"
            :disabled="isSubmitting"
            @click="closeModal"
          ></button>
        </div>

        <form novalidate @submit.prevent="confirm">
          <div class="modal-body">
            <p class="mb-3">{{ description }}</p>
            <div v-if="selectOptions.length" class="mb-3">
              <label for="estimateStatusTransitionSelect" class="form-label">{{ selectLabel }}</label>
              <select
                id="estimateStatusTransitionSelect"
                v-model="selectValue"
                class="form-select"
                :disabled="isSubmitting"
              >
                <option value="">選択しない</option>
                <option v-for="option in selectOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
            <label for="estimateStatusTransitionComment" class="form-label">
              {{ inputLabel }}
              <span v-if="isRequired" class="badge text-bg-danger">必須</span>
            </label>
            <textarea
              id="estimateStatusTransitionComment"
              ref="inputElement"
              v-model="inputValue"
              class="form-control"
              rows="4"
              :required="isRequired"
              :disabled="isSubmitting"
              :aria-invalid="Boolean(validationError)"
              :aria-describedby="validationError ? 'estimateStatusTransitionError' : undefined"
            ></textarea>
            <div
              v-if="validationError"
              id="estimateStatusTransitionError"
              class="text-danger mt-2"
            >
              {{ validationError }}
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="isSubmitting"
              @click="closeModal"
            >
              キャンセル
            </button>
            <button
              type="submit"
              class="btn"
              :class="confirmButtonClass"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? submittingLabel : confirmLabel }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick, ref } from 'vue';

export default {
  props: {
    isSubmitting: {
      type: Boolean,
      default: false
    }
  },
  emits: ['confirm'],
  setup(props, { emit }) {
    const isVisible = ref(false);
    const action = ref('');
    const title = ref('');
    const description = ref('');
    const inputLabel = ref('');
    const inputValue = ref('');
    const selectLabel = ref('');
    const selectOptions = ref([]);
    const selectValue = ref('');
    const isRequired = ref(false);
    const requiredMessage = ref('入力してください');
    const confirmLabel = ref('実行する');
    const submittingLabel = ref('処理中...');
    const confirmButtonClass = ref('btn-primary');
    const validationError = ref('');
    const inputElement = ref(null);

    const show = (options) => {
      action.value = options.action;
      title.value = options.title;
      description.value = options.description;
      inputLabel.value = options.inputLabel;
      inputValue.value = options.initialValue || '';
      selectLabel.value = options.selectLabel || '';
      selectOptions.value = options.selectOptions || [];
      selectValue.value = options.initialSelectValue || '';
      isRequired.value = Boolean(options.isRequired);
      requiredMessage.value = options.requiredMessage || '入力してください';
      confirmLabel.value = options.confirmLabel || '実行する';
      submittingLabel.value = options.submittingLabel || '処理中...';
      confirmButtonClass.value = options.confirmButtonClass || 'btn-primary';
      validationError.value = '';
      isVisible.value = true;

      nextTick(() => inputElement.value?.focus());
    };

    const closeModal = () => {
      if (props.isSubmitting) return;
      isVisible.value = false;
      validationError.value = '';
    };

    const confirm = () => {
      const value = inputValue.value.trim();
      if (isRequired.value && !value) {
        validationError.value = requiredMessage.value;
        inputElement.value?.focus();
        return;
      }

      validationError.value = '';
      emit('confirm', { action: action.value, value, selectValue: selectValue.value });
    };

    return {
      isVisible,
      title,
      description,
      inputLabel,
      inputValue,
      selectLabel,
      selectOptions,
      selectValue,
      isRequired,
      confirmLabel,
      submittingLabel,
      confirmButtonClass,
      validationError,
      inputElement,
      show,
      closeModal,
      confirm
    };
  }
};
</script>

<style scoped>
.estimate-status-transition-modal {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
  max-width: 560px;
}
</style>
